import { Alert, CircularProgress } from '@mui/material';
import { FunctionComponent, PropsWithChildren, useCallback, useState } from 'react';

type LoadingOptions = {
	message: string;
	failureMessage: string;
};

type LoadingState<T> = {
	load: () => Promise<T>;
	loading: string | undefined;
	error: string | undefined;
};

export function useLoadingState<T>(options: LoadingOptions, loadFunction: () => Promise<T>): LoadingState<T> {
	const [loading, setLoading] = useState<string>();
	const [error, setError] = useState<string>();

	const load = useCallback(async function () {
		try {
			setLoading(options.message);
			setError(undefined);
			return await loadFunction();
		} catch (err) {
			const message = (err instanceof Error) ? err.message : options.failureMessage;
			setError(message);
			throw err;
		} finally {
			setLoading(undefined);
		}
	}, []);

	return { loading, error, load };
}

type Props = PropsWithChildren<Pick<LoadingState<unknown>, 'loading' | 'error'>>;

export const LoadComponent: FunctionComponent<Props> = (props) => {
	if (props.error) {
		return <Alert severity='error'>{props.error}</Alert>;
	} else if (props.loading) {
		return <CircularProgress size={20} />;
	} else {
		return props.children;
	}
};